import { encryptSecret, decryptSecret, randomUrlToken } from './xero-oauth.ts'

export const SQUARE_OAUTH_SCOPES = [
  'MERCHANT_PROFILE_READ',
  'PAYMENTS_READ',
  'PAYMENTS_WRITE',
] as const

export const SQUARE_REQUIRED_SCOPES = SQUARE_OAUTH_SCOPES.join(' ')

export const SQUARE_CONNECTION_STATUSES = [
  'setup_required',
  'not_connected',
  'pending_location',
  'connected',
  'disconnected',
] as const

export type SquareConnectionStatus = (typeof SQUARE_CONNECTION_STATUSES)[number]
export type SquareEnvironment = 'sandbox' | 'production'

export interface SquareLocationOption {
  id: string
  name: string
  status?: string
  currency?: string
}

export interface PublicSquareConnection {
  status: SquareConnectionStatus
  environment: SquareEnvironment
  merchantId?: string
  locationId?: string
  locationName?: string
  locations?: SquareLocationOption[]
  connectedAt?: string | null
  setupRequired?: boolean
  missing?: string[]
  instructions?: string
  lastError?: string | null
}

export function canLaunchSquareFromAdmin(connection: PublicSquareConnection | null | undefined) {
  return connection?.status === 'connected' && Boolean(connection.locationId)
}

export function squareEnvironmentFrom(value?: string | null): SquareEnvironment {
  return value === 'production' ? 'production' : 'sandbox'
}

export function squareApiBase(environment: SquareEnvironment) {
  return environment === 'production'
    ? 'https://connect.squareup.com'
    : 'https://connect.squareupsandbox.com'
}

export function squareAuthorizeUrl(environment: SquareEnvironment) {
  return `${squareApiBase(environment)}/oauth2/authorize`
}

export function squareTokenUrl(environment: SquareEnvironment) {
  return `${squareApiBase(environment)}/oauth2/token`
}

export function squareOAuthSetupInstructions() {
  return [
    'Create a Square application and keep it in Sandbox until Lyle is present.',
    'Redirect URI must point at the square-oauth-callback Edge Function.',
    `Request scopes: ${SQUARE_REQUIRED_SCOPES}.`,
    'Store SQUARE_APPLICATION_ID, SQUARE_APPLICATION_SECRET, SQUARE_REDIRECT_URI, SQUARE_ENVIRONMENT, and SQUARE_WEBHOOK_SIGNATURE_KEY in Supabase Edge Function secrets.',
    'Do not connect a live Square account until Lyle authorises it from Autoglow admin.',
    'Square Reader (2nd generation) is launched through Square Point of Sale on the studio iPhone/iPad — Autoglow does not sync a service catalogue into Square.',
  ].join(' ')
}

export function buildSquareAuthorizeUrl(input: {
  applicationId: string
  redirectUri: string
  state: string
  environment: SquareEnvironment
  scopes?: string
  session?: boolean
}) {
  const params = new URLSearchParams({
    client_id: input.applicationId,
    scope: input.scopes ?? SQUARE_REQUIRED_SCOPES,
    session: 'false',
    state: input.state,
    redirect_uri: input.redirectUri,
  })
  return `${squareAuthorizeUrl(input.environment)}?${params.toString()}`
}

export function publicSquareConnectionFromUnknown(value: unknown): PublicSquareConnection | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const tokenKeys = [
    'refresh_token',
    'refreshToken',
    'access_token',
    'accessToken',
    'access_token_ciphertext',
    'refresh_token_ciphertext',
    'application_secret',
    'applicationSecret',
  ]
  if (tokenKeys.some((key) => key in record && record[key])) return null
  if (typeof record.status !== 'string') return null
  return record as unknown as PublicSquareConnection
}

export function squarePosIosUrl(input: {
  applicationId: string
  amountCents: number
  currency: 'AUD'
  callbackUrl: string
  locationId?: string
  notes: string
  referenceId: string
}) {
  const payload = {
    amount_money: {
      amount: input.amountCents,
      currency_code: input.currency,
    },
    callback_url: input.callbackUrl,
    client_id: input.applicationId,
    version: '1.3',
    notes: input.notes.slice(0, 500),
    location_id: input.locationId,
    options: {
      supported_tender_types: ['CREDIT_CARD'],
      auto_return: true,
      skip_receipt: false,
    },
  }
  return `square-commerce-v2://payment/create?data=${encodeURIComponent(JSON.stringify(payload))}`
}

export function squarePosAndroidUrl(input: {
  applicationId: string
  amountCents: number
  currency: 'AUD'
  callbackUrl: string
  locationId?: string
  notes: string
  fallbackUrl: string
}) {
  const parts = [
    'intent:#Intent',
    'action=com.squareup.pos.action.CHARGE',
    'package=com.squareup',
    `S.browser_fallback_url=${encodeURIComponent(input.fallbackUrl)}`,
    `S.com.squareup.pos.WEB_CALLBACK_URI=${encodeURIComponent(input.callbackUrl)}`,
    `S.com.squareup.pos.CLIENT_ID=${encodeURIComponent(input.applicationId)}`,
    'S.com.squareup.pos.API_VERSION=v2.0',
    `l.com.squareup.pos.TOTAL_AMOUNT=${input.amountCents}`,
    `S.com.squareup.pos.CURRENCY_CODE=${input.currency}`,
    'S.com.squareup.pos.TENDER_TYPES=com.squareup.pos.TENDER_CARD',
    `S.com.squareup.pos.NOTE=${encodeURIComponent(input.notes.slice(0, 500))}`,
    input.locationId ? `S.com.squareup.pos.LOCATION_ID=${encodeURIComponent(input.locationId)}` : '',
    'end',
  ].filter(Boolean)
  return parts.join(';')
}

export function buildSquarePosLaunch(input: {
  applicationId: string
  amountCents: number
  currency: 'AUD'
  callbackUrl: string
  locationId?: string
  bookingReference: string
  bookingId: string
  invoiceId?: string | null
}) {
  const notes = `Autoglow ${input.bookingReference}`
  return {
    amountCents: input.amountCents,
    currency: input.currency,
    bookingId: input.bookingId,
    bookingReference: input.bookingReference,
    invoiceId: input.invoiceId ?? null,
    iosUrl: squarePosIosUrl({
      applicationId: input.applicationId,
      amountCents: input.amountCents,
      currency: input.currency,
      callbackUrl: input.callbackUrl,
      locationId: input.locationId,
      notes,
      referenceId: input.bookingReference,
    }),
    androidUrl: squarePosAndroidUrl({
      applicationId: input.applicationId,
      amountCents: input.amountCents,
      currency: input.currency,
      callbackUrl: input.callbackUrl,
      locationId: input.locationId,
      notes,
      fallbackUrl: input.callbackUrl,
    }),
  }
}

export async function encryptSquareSecret(plaintext: string, secret: string) {
  return encryptSecret(plaintext, secret)
}

export async function decryptSquareSecret(payload: string, secret: string) {
  return decryptSecret(payload, secret)
}

export function newSquareOAuthState() {
  return randomUrlToken(24)
}

export function squareWebhookEvents() {
  return ['payment.created', 'payment.updated'] as const
}
